(() => {
  const setText = (root, selector, value) => {
    root.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  };

  const setButtonState = (button, available) => {
    if (!button) return;
    button.disabled = !available;
    button.setAttribute("aria-disabled", String(!available));
  };

  let cartReturnFocus = null;

  const money = (cents, currency = window.Shopify?.currency?.active || "USD") => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency,
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  };

  const cartElements = () => ({
    drawer: document.querySelector("[data-lumora-cart-drawer]"),
    backdrop: document.querySelector(".lumora-cart-backdrop"),
    status: document.querySelector("[data-lumora-cart-status]"),
    items: document.querySelector("[data-lumora-cart-items]"),
    footer: document.querySelector("[data-lumora-cart-footer]"),
    total: document.querySelector("[data-lumora-cart-total]"),
  });

  const openCart = (trigger) => {
    const { drawer, backdrop } = cartElements();
    if (!drawer || !backdrop) return;
    cartReturnFocus = trigger || document.activeElement;
    drawer.hidden = false;
    backdrop.hidden = false;
    drawer.removeAttribute("inert");
    drawer.setAttribute("aria-hidden", "false");
    document.body.dataset.lumoraCartOpen = "true";
    drawer.querySelector("[data-lumora-cart-close]")?.focus();
  };

  const closeCart = () => {
    const { drawer, backdrop } = cartElements();
    if (!drawer || !backdrop || drawer.hidden) return;
    drawer.setAttribute("aria-hidden", "true");
    drawer.setAttribute("inert", "");
    delete document.body.dataset.lumoraCartOpen;
    window.setTimeout(() => {
      if (drawer.getAttribute("aria-hidden") === "true") {
        drawer.hidden = true;
        backdrop.hidden = true;
      }
    }, 260);
    cartReturnFocus?.focus?.();
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const renderCart = (cart) => {
    const { items, footer, total, status } = cartElements();
    if (!items || !footer) return;

    document.querySelectorAll("[data-lumora-cart-count]").forEach((count) => {
      count.textContent = cart.item_count;
      count.setAttribute("aria-label", `${cart.item_count} items`);
    });
    items.replaceChildren();

    if (!cart.item_count) {
      const empty = element("div", "lumora-cart-drawer__empty");
      empty.dataset.lumoraCartEmpty = "";
      empty.append(element("p", "", "Your cart is currently empty."));
      const browse = element("button", "", "Continue shopping");
      browse.type = "button";
      browse.dataset.lumoraCartClose = "";
      empty.append(browse);
      items.append(empty);
      footer.hidden = true;
    } else {
      cart.items.forEach((item, index) => {
        const line = element("article", "lumora-cart-drawer__line");
        line.dataset.cartKey = item.key;
        if (item.image) {
          const image = element("img");
          image.src = item.image;
          image.alt = item.product_title;
          image.width = 110;
          image.height = 110;
          image.loading = "lazy";
          line.append(image);
        }
        const details = element("div");
        const title = element("a", "", item.product_title);
        title.href = item.url;
        details.append(title);
        if (item.variant_title && item.variant_title !== "Default Title") {
          details.append(element("small", "", item.variant_title));
        }
        details.append(element("strong", "", money(item.final_line_price, cart.currency)));
        const quantity = element("div", "lumora-cart-drawer__quantity");
        quantity.setAttribute("aria-label", "Quantity controls");
        [
          ["−", Math.max(0, item.quantity - 1), `Decrease ${item.product_title} quantity`, ""],
          ["+", item.quantity + 1, `Increase ${item.product_title} quantity`, ""],
          ["Remove", 0, `Remove ${item.product_title}`, "lumora-cart-drawer__remove"],
        ].forEach(([label, nextQuantity, ariaLabel, className], buttonIndex) => {
          if (buttonIndex === 1) quantity.append(element("span", "", String(item.quantity)));
          const button = element("button", className, label);
          button.type = "button";
          button.dataset.lumoraCartChange = "";
          button.dataset.line = String(index + 1);
          button.dataset.quantity = String(nextQuantity);
          button.setAttribute("aria-label", ariaLabel);
          quantity.append(button);
        });
        details.append(quantity);
        line.append(details);
        items.append(line);
      });
      footer.hidden = false;
      if (total) total.textContent = money(cart.total_price, cart.currency);
    }
    if (status) status.textContent = `${cart.item_count} item${cart.item_count === 1 ? "" : "s"} in cart.`;
  };

  const loadCart = async () => {
    const response = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("Unable to load cart");
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const initializeCart = () => {
    const { drawer, status } = cartElements();
    if (!drawer || drawer.dataset.lumoraInitialized === "true") return;
    drawer.dataset.lumoraInitialized = "true";

    document.addEventListener("click", async (event) => {
      const toggle = event.target.closest("[data-lumora-cart-toggle]");
      if (toggle) {
        event.preventDefault();
        openCart(toggle);
        try { await loadCart(); } catch { if (status) status.textContent = "Cart could not refresh. Use View full cart."; }
        return;
      }
      if (event.target.closest("[data-lumora-cart-close]")) {
        closeCart();
        return;
      }
      const change = event.target.closest("[data-lumora-cart-change]");
      if (!change) return;
      change.disabled = true;
      if (status) status.textContent = "Updating cart…";
      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ line: Number(change.dataset.line), quantity: Number(change.dataset.quantity) }),
        });
        if (!response.ok) throw new Error("Unable to update cart");
        renderCart(await response.json());
      } catch {
        change.disabled = false;
        if (status) status.textContent = "Cart could not update. Open the full cart to continue.";
      }
    });

    drawer.addEventListener("keydown", (event) => {
      if (event.key !== "Tab") return;
      const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')]
        .filter((node) => !node.hidden && node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  };

  const initializeProduct = (root) => {
    if (root.dataset.lumoraInitialized === "true") return;
    root.dataset.lumoraInitialized = "true";

    const select = root.querySelector("[data-variant-select]");
    const addButton = root.querySelector("[data-add-to-cart]");
    const stickyButton = root.querySelector("[data-sticky-add]");
    const addLabel = root.querySelector("[data-add-label]");
    const availableLabel = addLabel && !addButton.disabled ? addLabel.textContent.trim() : "Add to cart";
    const soldOutLabel = addLabel && addButton.disabled ? addLabel.textContent.trim() : "Sold out";

    const updateVariant = () => {
      const option = select?.selectedOptions[0];
      if (!option) return;
      const available = option.dataset.available === "true";
      const price = option.dataset.price || "";
      const compare = option.dataset.compare || "";
      setText(root, "[data-product-price], [data-button-price], [data-mobile-price]", price);
      root.querySelectorAll("[data-compare-price]").forEach((element) => {
        element.textContent = compare;
        element.hidden = !compare;
      });
      if (addLabel) addLabel.textContent = available ? availableLabel : soldOutLabel;
      if (stickyButton) stickyButton.textContent = available ? availableLabel : soldOutLabel;
      setButtonState(addButton, available);
      setButtonState(stickyButton, available);
    };

    select?.addEventListener("change", updateVariant);

    const galleryImages = Array.from(root.querySelectorAll("[data-gallery-image]"));
    let activeGalleryIndex = Math.max(0, galleryImages.findIndex((image) => !image.hidden));
    const showGalleryImage = (index) => {
      if (!galleryImages.length) return;
      activeGalleryIndex = (index + galleryImages.length) % galleryImages.length;
      galleryImages.forEach((image, imageIndex) => {
        image.hidden = imageIndex !== activeGalleryIndex;
      });
    };
    root.querySelector("[data-gallery-prev]")?.addEventListener("click", () => showGalleryImage(activeGalleryIndex - 1));
    root.querySelector("[data-gallery-next]")?.addEventListener("click", () => showGalleryImage(activeGalleryIndex + 1));

    root.querySelectorAll("[data-fallback-src]").forEach((button) => {
      button.addEventListener("click", () => {
        const stageImage = root.querySelector("[data-gallery-stage] img");
        if (stageImage) {
          stageImage.src = button.dataset.fallbackSrc;
          stageImage.removeAttribute("srcset");
          stageImage.removeAttribute("sizes");
        }
        root.querySelectorAll("[data-fallback-src]").forEach((control) => {
          const active = control === button;
          control.classList.toggle("is-active", active);
          control.setAttribute("aria-pressed", String(active));
        });
      });
    });

    root.querySelector(".lumora-product-form")?.addEventListener("submit", async (event) => {
      event.preventDefault();
      const form = event.currentTarget;
      const submitButton = form.querySelector("[data-add-to-cart]");
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute("aria-busy", "true");
      }
      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error("Unable to add item");
        await loadCart();
        openCart(submitButton);
      } catch {
        form.submit();
      } finally {
        if (submitButton) {
          submitButton.removeAttribute("aria-busy");
          updateVariant();
        }
      }
    });

    updateVariant();
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll("[data-lumora-product]").forEach(initializeProduct);
    initializeCart();
    scope.querySelectorAll("[data-mobile-menu] nav a").forEach((link) => {
      link.addEventListener("click", () => link.closest("details")?.removeAttribute("open"));
    });
  };

  document.addEventListener("DOMContentLoaded", () => initialize());
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      document.querySelectorAll("[data-mobile-menu][open]").forEach((menu) => menu.removeAttribute("open"));
      closeCart();
    }
  });
})();
